import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ICancelationService } from '@shared/interfaces/cancelation-service.interface';
import { IDepartment } from '@shared/interfaces/deparment.interface';
import { IMunicipality } from '@shared/interfaces/municipality.interface';
import { PaginatorResult } from '@shared/interfaces/paginator_interface';
import { IParametersConfig } from '@shared/interfaces/parameters-config.interface';
import { IServiceDetail, IServiceType, IStatusService } from '@shared/interfaces/service-detail.interface';
import { ApiResponse } from '@shared/interfaces/wrap-api-response.interface';
import { Observable, Observer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CreateServicePayload, CreateServiceResult } from './interfaces/create-service';
import { DisabilityType } from './interfaces/disabilitie-interface';
import { IGenerateTransactionPaymentDto } from './interfaces/generate-transaction-payment';
import { DevicePosition } from './interfaces/map-interface';
import { IPaymentInfo } from './interfaces/payment_interface';
import { ChooseVehicleModelResult, RecommendedVehicleModel } from './interfaces/recommended-vehicle.interface';
import {
	AcceptServicePayload,
	AcceptServiceResponse,
	ApiResponseList,
	AutocompletePrediction,
	CancelServiceResponse,
	PlaceDetailsResult,
	ServiceFilters,
	Servicio,
	BulkUploadResult
} from './interfaces/services-interface';
import { ServiceStep } from './interfaces/stop-interfaces';
import { ServiceCostSummary } from './interfaces/summary-cost-interface';
import { ServiceTimelineStatus } from './interfaces/timeline-status';
import { TripType } from './interfaces/trip-type.interface';
import { IUpdateServiceDto } from './interfaces/update-service-dto.interface';
import { ServiceRatingResult } from './interfaces/service-rating.interface';
import { ITrip } from '@shared/interfaces/trip.interface';
import { People } from '@shared/interfaces/people.interface';

@Injectable({
	providedIn: 'root'
})
export class ServicesService {
	private http = inject(HttpClient);
	private url = environment.API_DOMAIN + environment.SERVICES.SERVICES_GESTION;
	private fuecUrl = environment.API_DOMAIN + environment.SERVICES.FUEC;
	//private autocompleteService: google.maps.places.AutocompleteService;
	private placesService: google.maps.places.PlacesService;
	private distanceMatrixService: google.maps.DistanceMatrixService;

	constructor() {}

	private _initServices(): void {
		// Verificar que Google Maps está disponible
		if (typeof google !== 'undefined' && google.maps && google.maps.places) {
			const dummyDiv = document.createElement('div');
			this.placesService = new google.maps.places.PlacesService(dummyDiv);
			this.distanceMatrixService = new google.maps.DistanceMatrixService();
		} else {
			console.error('Google Maps o Places no está disponible');
			console.log('google.maps check:', typeof google !== 'undefined' && google.maps);
			console.log('google.maps.places check:', typeof google !== 'undefined' && google.maps && google.maps.places);
		}
	}

	public getStatusServices(): Observable<ApiResponseList<IStatusService>> {
		return this.http.get<ApiResponseList<IStatusService>>(`${this.url}/services/status`);
	}
	public getServices(filters?: Partial<ServiceFilters>): Observable<ApiResponseList<Servicio>> {
		let params = new HttpParams();
		if (filters) {
			Object.entries(filters).forEach(([key, value]) => {
				if (value !== null && value !== undefined) {
					params = params.append(key, String(value));
				}
			});
		}
		return this.http.get<ApiResponseList<Servicio>>(`${this.url}/services`, { params });
	}
	public getServiceTypes(languageId: number = 1): Observable<ApiResponseList<IServiceType>> {
		const params = new HttpParams().set('languageId', languageId.toString());
		return this.http.get<ApiResponseList<IServiceType>>(`${this.url}/services/types`, { params });
	}
	public getServiceDetail(id: number): Observable<ApiResponse<IServiceDetail>> {
		return this.http.get<ApiResponse<IServiceDetail>>(`${this.url}/services/${id}`);
	}
	public getServiceBeneficiary(id: number): Observable<ApiResponseList<People>> {
		return this.http.get<ApiResponseList<People>>(`${this.url}/services/${id}/people-trip-enterprises`);
	}
	public getServiceDetailWithToken(id: number, token: string): Observable<ApiResponse<IServiceDetail>> {
		const params = new HttpParams().set('token', token);
		return this.http.get<ApiResponse<IServiceDetail>>(`${this.url}/services/${id}/detail-with-token`, { params });
	}
	public rateServiceWithToken(
		id: number,
		token: string,
		rating: number,
		comment: string
	): Observable<ApiResponse<ServiceRatingResult>> {
		const params = new HttpParams().set('token', token);
		return this.http.post<ApiResponse<ServiceRatingResult>>(
			`${this.url}/services/${id}/rating-with-token`,
			{ rating, comment },
			{ params }
		);
	}
	public getServiceTimeline(id: number): Observable<ApiResponseList<ServiceTimelineStatus>> {
		return this.http.get<ApiResponseList<ServiceTimelineStatus>>(`${this.url}/services/${id}/timeline/status`);
	}
	public getServiceSteps(id: number): Observable<ApiResponseList<ServiceStep>> {
		return this.http.get<ApiResponseList<ServiceStep>>(`${this.url}/services/${id}/steps`);
	}
	public getDeviceCurrentPosition(id: number): Observable<ApiResponse<DevicePosition>> {
		return this.http.get<ApiResponse<DevicePosition>>(`${this.url}/services/${id}/device-current-position`);
	}
	public getDisabilityTypes(languageId: number = 1): Observable<ApiResponseList<DisabilityType>> {
		const params = new HttpParams().set('languageId', languageId.toString());
		return this.http.get<ApiResponseList<DisabilityType>>(`${this.url}/disabilities/types`, { params });
	}
	/*public getGoogleAutocompleteOld(input: string): Observable<AutocompletePrediction[]> {
		this._initServices();
		return new Observable((observer: Observer<AutocompletePrediction[]>) => {
			this.autocompleteService.getPlacePredictions(
				{ input: input, componentRestrictions: { country: 'CO' } },
				(predictions, status) => {
					if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
						observer.next(predictions as AutocompletePrediction[]);
						observer.complete();
					} else {
						observer.error(`Google Autocomplete Error: ${status}`);
					}
				}
			);
		});
	}*/
	public getGoogleAutocomplete(input: string): Observable<AutocompletePrediction[]> {
		return new Observable((observer) => {
			(async () => {
				try {
					const { AutocompleteSuggestion } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;

					const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
						input,
						includedRegionCodes: ['CO']
					});

					console.log('🔍 DEBUG - Google API Response:', response);
					console.log('🔍 DEBUG - Total suggestions:', response.suggestions.length);

					// Revisar todas las sugerencias para ver qué contienen
					response.suggestions.forEach((suggestion, index) => {
						console.log(`🔍 DEBUG - Suggestion ${index}:`, suggestion);
						console.log(`🔍 DEBUG - Has placePrediction:`, !!suggestion.placePrediction);
						if (suggestion.placePrediction) {
							console.log(`🔍 DEBUG - Place details:`, {
								text: suggestion.placePrediction.text,
								placeId: suggestion.placePrediction.placeId
							});
						}
					});

					// Solo incluir sugerencias que tengan place_id (solo placePrediction)
					const predictions: AutocompletePrediction[] = response.suggestions
						.filter((suggestion) => suggestion.placePrediction) // Solo sugerencias con place_id
						.map((suggestion) => {
							const place = suggestion.placePrediction!;
							const prediction = {
								description: place.text?.text ?? '',
								place_id: place.placeId ?? ''
							};
							console.log('🔍 DEBUG - Processed prediction:', prediction);
							return prediction;
						})
						.filter((prediction) => prediction.description && prediction.place_id); // Validar que tenga ambos campos

					console.info('✅ Google Autocomplete - Total suggestions recibidas:', response.suggestions.length);
					console.info('✅ Google Autocomplete - Place predictions con place_id:', predictions.length);
					console.log('✅ Final predictions:', predictions);

					observer.next(predictions);
					observer.complete();
				} catch (error) {
					console.error('getGoogleAutocomplete - Error:', error);
					observer.error(`Google Autocomplete Error: ${error}`);
				}
			})();
		});
	}

	// --- MÉTODO getGooglePlaceDetails REESCRITO CON EL SDK ---
	public getGooglePlaceDetails(placeId: string): Observable<PlaceDetailsResult> {
		this._initServices();

		if (!this.placesService) {
			return new Observable((observer) => {
				observer.error('Google Places Service no está disponible');
			});
		}

		return new Observable((observer: Observer<PlaceDetailsResult>) => {
			this.placesService!.getDetails(
				{ placeId: placeId, fields: ['geometry.location', 'formatted_address', 'address_components'] },
				(place, status) => {
					if (status === google.maps.places.PlacesServiceStatus.OK && place) {
						// CAMBIO CLAVE: Construimos el objeto manualmente
						const formattedResult: PlaceDetailsResult = {
							// Mapeamos las propiedades que sí coinciden
							address_components: place.address_components,
							formatted_address: place.formatted_address,

							// Corregimos la geometría llamando a las funciones lat() y lng()
							geometry: {
								location: {
									lat: place.geometry.location.lat(), // <--- Se llama como función
									lng: place.geometry.location.lng() // <--- Se llama como función
								}
							}
						};

						observer.next(formattedResult);
						observer.complete();
					} else {
						observer.error(`Google Place Details Error: ${status}`);
					}
				}
			);
		});
	}
	public getDistanceAndDuration(
		originLat: number,
		originLng: number,
		destinationLat: number,
		destinationLng: number
	): Observable<{ distance: number; duration: number; distanceText: string; durationText: string }> {
		return new Observable((observer) => {
			// Esperar a que Google Maps esté disponible
			const initService = () => {
				if (typeof google !== 'undefined' && google.maps && google.maps.DistanceMatrixService) {
					this.distanceMatrixService = new google.maps.DistanceMatrixService();
					this._calculateDistance(originLat, originLng, destinationLat, destinationLng, observer);
				} else {
					console.error('Google Maps DistanceMatrixService no está disponible');
					observer.error('Google Maps no está disponible');
				}
			};

			// Si ya está inicializado, calcular directamente
			if (this.distanceMatrixService) {
				this._calculateDistance(originLat, originLng, destinationLat, destinationLng, observer);
			} else {
				initService();
			}
		});
	}

	private _calculateDistance(
		originLat: number,
		originLng: number,
		destinationLat: number,
		destinationLng: number,
		observer: Observer<{ distance: number; duration: number; distanceText: string; durationText: string }>
	): void {
		if (!this.distanceMatrixService) {
			observer.error('Distance Matrix Service no está disponible');
			return;
		}

		const request: google.maps.DistanceMatrixRequest = {
			origins: [{ lat: originLat, lng: originLng }],
			destinations: [{ lat: destinationLat, lng: destinationLng }],
			travelMode: google.maps.TravelMode.DRIVING,
			unitSystem: google.maps.UnitSystem.METRIC
		};

		this.distanceMatrixService!.getDistanceMatrix(request, (response, status) => {
			if (
				status === google.maps.DistanceMatrixStatus.OK &&
				response &&
				response.rows.length > 0 &&
				response.rows[0].elements.length > 0
			) {
				const element = response.rows[0].elements[0];
				if (element.status === google.maps.DistanceMatrixElementStatus.OK) {
					const distanceValue = element.distance?.value;
					const durationValue = element.duration?.value;
					const distanceText = element.distance?.text;
					const durationText = element.duration?.text;

					if (
						distanceValue !== undefined &&
						durationValue !== undefined &&
						distanceText !== undefined &&
						durationText !== undefined
					) {
						observer.next({
							distance: distanceValue,
							duration: durationValue,
							distanceText: distanceText,
							durationText: durationText
						});
						observer.complete();
					} else {
						observer.error('Datos de distancia/duración incompletos de Google Maps.');
					}
				} else {
					observer.error(`Distance Matrix Element Status: ${element.status}`);
				}
			} else {
				observer.error(`Distance Matrix API Status: ${status} o respuesta incompleta.`);
			}
		});
	}

	public createService(payload: CreateServicePayload): Observable<ApiResponse<CreateServiceResult>> {
		// <-- ¡ESTE ES EL CAMBIO!
		return this.http.post<ApiResponse<CreateServiceResult>>(`${this.url}/services`, payload);
	}

	public bulkUploadServices(file: File): Observable<ApiResponse<BulkUploadResult>> {
		const formData = new FormData();
		formData.append('file', file);
		return this.http.post<ApiResponse<BulkUploadResult>>(`${this.url}/services/bulk-upload`, formData);
	}

	public downloadBulkUploadTemplate(): Observable<Blob> {
		return this.http.get(`${this.url}/services/bulk-upload/template`, {
			responseType: 'blob'
		});
	}

	public cancelService(id: number): Observable<ApiResponse<CancelServiceResponse>> {
		return this.http.put<ApiResponse<CancelServiceResponse>>(`${this.url}/services/${id}/cancel`, {});
	}
	public getServiceCostSummary(id: number): Observable<ApiResponse<ServiceCostSummary>> {
		return this.http.get<ApiResponse<ServiceCostSummary>>(`${this.url}/services/${id}/summary/cost`);
	}
	public getFinishedServices(filters?: Partial<ServiceFilters>): Observable<PaginatorResult<IServiceDetail>> {
		let params = new HttpParams();
		if (filters) {
			Object.entries(filters).forEach(([key, value]) => {
				if (value !== null && value !== undefined) {
					params = params.append(key, String(value));
				}
			});
		}
		return this.http.get<PaginatorResult<IServiceDetail>>(`${this.url}/services/finished`, { params });
	}

	public getExpiredUnmanagedServices(filters?: Partial<ServiceFilters>): Observable<PaginatorResult<IServiceDetail>> {
		let params = new HttpParams();
		if (filters) {
			Object.entries(filters).forEach(([key, value]) => {
				if (value !== null && value !== undefined) {
					params = params.append(key, String(value));
				}
			});
		}
		return this.http.get<PaginatorResult<IServiceDetail>>(`${this.url}/services/expired-unmanaged`, { params });
	}

	public getRecommendedVehicleModels(serviceId: number): Observable<ApiResponseList<RecommendedVehicleModel>> {
		return this.http.get<ApiResponseList<RecommendedVehicleModel>>(
			`${this.url}/services/${serviceId}/recommended/vehicle/models`
		);
	}
	public chooseVehicleModel(serviceId: number, modelId: number): Observable<ApiResponse<ChooseVehicleModelResult>> {
		const payload = { modelId: modelId };
		return this.http.put<ApiResponse<ChooseVehicleModelResult>>(
			`${this.url}/services/${serviceId}/choice/model/vehicle`,
			payload
		);
	}
	public getDepartments(query: string): Observable<ApiResponseList<IDepartment>> {
		const params = new HttpParams().set('query', query);
		return this.http.get<ApiResponseList<IDepartment>>(`${this.url}/departments`, { params });
	}

	public getMunicipalities(query: string): Observable<ApiResponseList<IMunicipality>> {
		const params = new HttpParams().set('query', query);
		return this.http.get<ApiResponseList<IMunicipality>>(`${this.url}/municipalities`, { params });
	}

	public getPendingRatings(page?: number, pageSize?: number): Observable<PaginatorResult<ITrip>> {
		let params = new HttpParams();
		if (page) {
			params = params.set('page', page.toString());
		}
		if (pageSize) {
			params = params.set('pageSize', pageSize.toString());
		}
		return this.http.get<PaginatorResult<ITrip>>(`${this.url}/services/pending/rating`, { params });
	}
	public getTripTypes(): Observable<ApiResponseList<TripType>> {
		const params = new HttpParams().set('languageId', '1');
		return this.http.get<ApiResponseList<TripType>>(`${this.url}/trips/trip/types`, { params });
	}
	public getPaymentInfo(serviceId: number): Observable<ApiResponseList<IPaymentInfo>> {
		return this.http.get<ApiResponseList<IPaymentInfo>>(`${this.url}/services/${serviceId}/payment/info`);
	}

	public generateTransactionPayment(serviceId: number): Observable<ApiResponse<IGenerateTransactionPaymentDto>> {
		return this.http.get<ApiResponse<IGenerateTransactionPaymentDto>>(
			`${this.url}/services/${serviceId}/payment/transaction`
		);
	}
	public getParameterConfig(parameterCode: string): Observable<ApiResponse<IParametersConfig>> {
		return this.http.get<ApiResponse<IParametersConfig>>(`${this.url}/parameters/code/${parameterCode}`);
	}

	public generateFuec(
		serviceId: number | string,
		serviceTripId: number | string
	): Observable<ApiResponse<{ pdfBase64: string }>> {
		const url = `${this.fuecUrl}/generate-process-service/${serviceId}/${serviceTripId}`;
		return this.http.post<ApiResponse<{ pdfBase64: string }>>(url, {});
	}

	public updateService(serviceId: number, payload: IUpdateServiceDto): Observable<ApiResponse<{ id: number }>> {
		return this.http.patch<ApiResponse<{ id: number }>>(`${this.url}/services/${serviceId}`, payload);
	}

	public getCancellationReasons(serviceId: number): Observable<ApiResponseList<ICancelationService>> {
		return this.http.get<ApiResponseList<ICancelationService>>(`${this.url}/services/${serviceId}/cancelations`);
	}

	/**
	 * Acepta un servicio asignando un conductor y vehículo
	 * @param payload - Datos del servicio, conductor, vehículo y dirección del viaje
	 * @returns Observable con la respuesta de la aceptación del servicio
	 */
	public acceptService(payload: AcceptServicePayload): Observable<ApiResponse<AcceptServiceResponse>> {
		return this.http.post<ApiResponse<AcceptServiceResponse>>(`${this.url}/services/accept`, payload);
	}

	public exportFinishedServicesToExcel(filters?: Partial<ServiceFilters>): Observable<Blob> {
		let params = new HttpParams();
		if (filters) {
			Object.entries(filters).forEach(([key, value]) => {
				if (value !== null && value !== undefined) {
					params = params.append(key, String(value));
				}
			});
		}
		return this.http.get(`${this.url}/services/finished/download`, {
			params,
			responseType: 'blob'
		});
	}

	public exportExpiredUnmanagedServicesToExcel(filters?: Partial<ServiceFilters>): Observable<Blob> {
		let params = new HttpParams();
		if (filters) {
			Object.entries(filters).forEach(([key, value]) => {
				if (value !== null && value !== undefined) {
					params = params.append(key, String(value));
				}
			});
		}
		return this.http.get(`${this.url}/services/expired-unmanaged/download`, {
			params,
			responseType: 'blob'
		});
	}

	public exportServicesToExcel(filters?: Partial<ServiceFilters>): Observable<Blob> {
		let params = new HttpParams();
		if (filters) {
			Object.entries(filters).forEach(([key, value]) => {
				if (value !== null && value !== undefined) {
					if (Array.isArray(value)) {
						value.forEach((v) => (params = params.append(key, String(v))));
					} else {
						params = params.append(key, String(value));
					}
				}
			});
		}
		return this.http.get(`${this.url}/services/download-excel`, {
			params,
			responseType: 'blob'
		});
	}
}
